import { RisClient, RisError, type ViewerSession } from './client';

/**
 * The ris session of this page (viewer contract §3): the typed client with the launch ticket, and
 * `GET /session` (who, which perms, which studies, the org branding). Null in the standalone bench.
 */

export const PERM = {
  open: 'viewer:open',
  annotate: 'viewer:annotate',
  upload: 'pacs:upload:write',
  export: 'pacs:studies:export',
  reports: 'ris:reports:read',
  share: 'pacs:shares:write',
} as const;

const AUTH_FAILURE_STATUSES = new Set([401, 403]);

let client: RisClient | null = null;
let session: ViewerSession | null = null;

/** Opens the session; throws what the client throws (RisError on 4xx/5xx, TypeError offline). */
export async function startSession(apiRoot: string, ticket: string | null | (() => string | null)): Promise<ViewerSession> {
  const next = new RisClient(apiRoot, ticket);
  const opened = await next.session();
  client = next;
  session = opened;
  return opened;
}

/** The ris client, or null when the viewer runs without ris. */
export function risClient(): RisClient | null {
  return client;
}

export function risSession(): ViewerSession | null {
  return session;
}

/** The ticket was issued to the patient by the patient portal: the viewer builds its patient layout. */
export function isPatientSession(): boolean {
  return session?.patient === true;
}

/** The ticket was issued by the results portal to a referring doctor: the viewer builds its referrer layout. */
export function isReferrerSession(): boolean {
  return session?.referrer === true;
}

/** The watermark text ris set for a referrer ticket, or null. */
export function sessionWatermark(): string | null {
  const text = session?.watermark;
  return typeof text === 'string' && text.trim() !== '' ? text.trim() : null;
}

export function hasPerm(perm: string): boolean {
  return session?.perms.includes(perm) ?? false;
}

/** Studies the ticket covers (annotation sets and key images are per study); a hub token covers any. */
export function isSessionStudy(studyUID: string): boolean {
  if (!session) return false;
  return session.studies.length === 0 || session.studies.includes(studyUID);
}

/** The session was refused: missing, expired or revoked ticket. */
export function isAuthFailure(error: unknown): boolean {
  return error instanceof RisError && AUTH_FAILURE_STATUSES.has(error.status);
}
